import { Icon } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import * as React from 'react';
import { Text, Tooltip } from 'vet-bones/components';
import { getLocalName, isNotEqual } from 'vet-bones/utils';

import { TEST_IDS } from 'src/constants/testIds';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  Mapping,
  MappingIdentifierPart,
  MappingIdentifierType,
} from 'src/containers/ProjectDiagram/types/data';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { isResource } from 'src/containers/ProjectDiagram/utils/data';
import { setMappingPrimaryIdentifier } from 'src/containers/ProjectDiagram/utils/mapping/setMappingPrimaryIdentifier';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { getResourceValue } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { useAddPartToCompositeIdentifier } from 'src/containers/ProjectMapping/hooks/compositePrimaryIdentifiers';
import {
  useMappingFromRoute,
  usePrevMappingFromRoute,
} from 'src/containers/ProjectMapping/hooks/useMappingFromRoute';
import { ProjectMappingPrimaryIdentifierMultiSelect } from 'src/containers/ProjectMapping/ProjectMappingPrimaryIdentifierMultiSelect';
import { MultiSelectIdentifierItem } from 'src/containers/ProjectMapping/types';
import { isDraftDiagramSelector } from 'src/selectors/diagramSnapshot';
import { projectNodeDataSelector } from 'src/selectors/projectNodeData';
import { projectResourceFieldItemsSelector } from 'src/selectors/projectResource';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectClassMappingPrimaryIdentifierRow: React.VFC = () => {
  const { projectId } = useRouterParams();
  const diagram = useDiagram();
  const mapping = useMappingFromRoute() as Mapping & { to: string };
  const prevMapping = usePrevMappingFromRoute();
  const isDraftDiagram = useReduxSelector(isDraftDiagramSelector(projectId));
  const classLocalName = getLocalName(getModelConceptClassValue(mapping.to));
  // projectNodeDataSelector is a factory with a non-serializable arg, so it
  // can't be memoized at the source; memoize the instance here instead.
  const resourceDataSelector = React.useMemo(
    () => projectNodeDataSelector(projectId, mapping.from, isResource),
    [projectId, mapping.from]
  );
  const resourceData = useReduxSelector(resourceDataSelector);
  const fieldItems = useReduxSelector(
    projectResourceFieldItemsSelector(
      projectId,
      getResourceValue(mapping.from),
      resourceData?.type
    )
  ).map((field) => ({
    text: field.text,
    type: MappingIdentifierType.FIELD,
    value: field.value,
  }));

  const currentIdentifierItems: MultiSelectIdentifierItem[] =
    mapping.primaryIdentifier.parts.map((identifier) => {
      const fieldItem = fieldItems.find(
        (item) => item.value === identifier.value
      );
      return {
        ...identifier,
        text: fieldItem?.text || identifier.value,
      };
    });

  const handleAddPart = useAddPartToCompositeIdentifier(
    mapping.key,
    mapping.primaryIdentifier
  );

  const handleRemovePart = (
    _item: MultiSelectIdentifierItem,
    index: number
  ) => {
    if (!diagram) {
      return;
    }

    const updatedPrimaryIdentifierParts: MappingIdentifierPart[] = [
      ...mapping.primaryIdentifier.parts,
    ];

    updatedPrimaryIdentifierParts.splice(index, 1);

    commitDiagramModel(
      diagram,
      setMappingPrimaryIdentifier({
        key: mapping.key,
        primaryIdentifier: {
          baseIri: mapping.primaryIdentifier.baseIri,
          parts: updatedPrimaryIdentifierParts,
        },
      })
    );
  };

  const handleUpdateIri = (updatedIri: string | null) => {
    if (!diagram) {
      return;
    }
    commitDiagramModel(
      diagram,
      setMappingPrimaryIdentifier({
        key: mapping.key,
        primaryIdentifier: {
          baseIri: updatedIri,
          parts: mapping.primaryIdentifier.parts,
        },
      })
    );
  };

  const highlighted =
    isDraftDiagram &&
    (prevMapping
      ? isNotEqual(mapping.primaryIdentifier, prevMapping.primaryIdentifier)
      : Boolean(mapping.primaryIdentifier.parts.length));

  return (
    <>
      <div
        className={classNames(
          'sd-project-mapping-grid-cell',
          'sd-project-mapping-grid-cell--border-right',
          'sd-draft-highlight-candidate',
          { 'sd-draft-highlight-candidate--highlighted': highlighted }
        )}
      >
        <div className="sd-project-mapping-grid-cell-left">
          <Text>{copy.components.projectMapping.primaryIdentifier.label}</Text>
          <Tooltip
            content={copy.components.projectMapping.primaryIdentifier.help}
          >
            <Icon icon={IconNames.INFO_SIGN} />
          </Tooltip>
        </div>
      </div>

      <div
        className={classNames(
          'sd-project-mapping-grid-cell',
          'sd-project-mapping-grid-cell--wide',
          'sd-draft-highlight-candidate',
          { 'sd-draft-highlight-candidate--highlighted': highlighted }
        )}
        data-testid={TEST_IDS.projectMapping.primaryIdentifier.selectorCell}
      >
        <ProjectMappingPrimaryIdentifierMultiSelect
          classLocalName={classLocalName}
          currentIdentifier={mapping.primaryIdentifier}
          currentItems={currentIdentifierItems}
          fieldItems={fieldItems}
          onRemove={handleRemovePart}
          onSelect={handleAddPart}
          onUpdateIri={handleUpdateIri}
          resourceData={resourceData}
        />
      </div>
    </>
  );
};
